import React, { useId, useLayoutEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from './cn'
import { IconButton } from './icon-button'

export interface KiaoModalProps {
  open: boolean
  onClose: () => void
  title: React.ReactNode
  subtitle?: React.ReactNode
  /** × 關閉鈕的 aria-label（必填，由呼叫端帶 i18n 字串） */
  closeLabel: string
  /**
   * Explicit accessible name for the dialog. Pass this when the visual
   * `.km-head` (which normally names the dialog via `aria-labelledby`) is
   * collapsed — e.g. the `.is-done` flash hides the head, so without an
   * `aria-label` the dialog would announce with no name. When omitted the
   * dialog is named by its `<h3>{title}</h3>` as usual.
   */
  ariaLabel?: string
  children: React.ReactNode
  /**
   * Pinned action row below the scrolling body. Used by the drawer variant,
   * where the body is long enough that buttons at its end would be off-screen.
   */
  footer?: React.ReactNode
  className?: string
  /**
   * `form`: the default width or the wider 640px layout. `dialog`: the confirm
   * width, or `--measure-dialog-wide` for a dialog that carries content (a
   * field, a quoted entry) rather than one sentence.
   */
  size?: 'default' | 'wide'
  /** Keep a supplied footer visible while the body owns vertical scrolling. */
  scrollBody?: boolean
  /**
   * Optional initial focus target. This lets modal callers prioritize a primary
   * action such as a file picker over the generic first form field, without
   * competing with the modal's focus lifecycle via timers.
   */
  initialFocusRef?: React.RefObject<HTMLElement | null>
  /**
   * Delay initial focus until a caller has finished resetting conditional
   * content. This avoids targeting a focusable element that is about to
   * unmount during an open transition.
   */
  initialFocusReady?: boolean
  /**
   * Replaces the × entirely while the dialog cannot be closed — e.g. a status
   * word during an upload the user must cancel deliberately.
   *
   * A DISABLED × is not an acceptable substitute: it still reads as an exit, so
   * people aim at it and nothing happens (design-guideline §7.5 — a disabled
   * control must drop the interaction affordance, not just the interaction).
   * When this is set the caller is expected to make `onClose` a no-op too, so
   * Escape and backdrop clicks agree with what the header shows.
   */
  headAccessory?: React.ReactNode
  /**
   * `dialog` is the App surface's small centred dialog (a pick list, a one-field
   * add): the confirm dialog's look — its `--*-confirm` tokens, no ×, the title
   * at the confirm size, actions right-aligned with no rule above. Escape and the
   * backdrop still close it. Opt-in: the default `form` look is what the survey
   * page and the web app use, and it does not change.
   */
  variant?: 'form' | 'dialog'
  /**
   * Drop the body's side padding so content that brings its own `--m-gutter`
   * (a `List`) runs to the dialog edges.
   */
  flushBody?: boolean
  /**
   * `dialog` only: a × at the end of the title row, named by `closeLabel`. For a
   * dialog the person fills in, where leaving without an action button must
   * still be obvious; a confirm dialog leaves it out.
   */
  closeButton?: boolean
}

/** 通用表單對話框外殼（.kiao-modal）：標題 + 右上 × 關閉 + 內容自訂。
 *  × 固定於 .km-head（brand-spec 刪除語意：彈窗右上 × = 關閉 UI）。 */
export function KiaoModal({
  open,
  onClose,
  title,
  subtitle,
  closeLabel,
  ariaLabel,
  children,
  footer,
  className,
  initialFocusRef,
  initialFocusReady = true,
  size = 'default',
  scrollBody = false,
  headAccessory,
  variant = 'form',
  flushBody = false,
  closeButton = false,
}: KiaoModalProps) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()

  // A `<dialog>` is `display: none` until it is opened, so the whole dialog —
  // its footer buttons included — is present in the DOM but absent from the
  // accessibility tree between the commit and the moment this runs. A passive
  // effect leaves that gap open across a task boundary, which is long enough
  // for anything reading the DOM in between to see the text and miss the
  // controls. A layout effect closes it inside the same commit.
  useLayoutEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open) {
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal()
        else dialog.setAttribute('open', '')
        // showModal() focuses the first focusable descendant (a list row, the ×),
        // and macOS WebKit paints the keyboard ring on it even after a click.
        // The dialog itself takes focus instead; Tab reaches the controls.
        dialog.focus({ preventScroll: true })
      }

      if (initialFocusReady) {
        const initialField = dialog.querySelector<HTMLElement>(
          '[data-kiao-modal-body] input:not([type="hidden"]):not([disabled]), [data-kiao-modal-body] select:not([disabled]), [data-kiao-modal-body] textarea:not([disabled]), [data-kiao-modal-body] [role="combobox"]:not([disabled])'
        )
        const initialFocusTarget = initialFocusRef?.current ?? initialField
        initialFocusTarget?.focus({ preventScroll: true })
      }
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [initialFocusReady, open])

  if (!open) return null

  const ariaName = ariaLabel ? { 'aria-label': ariaLabel } : { 'aria-labelledby': titleId }
  const handleCancel = (event: React.SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault()
    onClose()
  }
  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    /* 點背景 = 關閉（dialog 本體即 backdrop 目標） */
    if (event.target === ref.current) onClose()
  }

  if (variant === 'dialog') {
    return (
      <dialog
        ref={ref}
        data-kiao-modal
        data-kiao-modal-variant="dialog"
        tabIndex={-1}
        className={cn(
          'm-auto overflow-hidden outline-none rounded-[var(--radius-dialog)] border-[length:var(--border-width-confirm)] border-[color:var(--border-confirm)] bg-background p-0 text-foreground shadow-[var(--shadow-confirm)] backdrop:bg-black/40',
          size === 'wide'
            ? 'w-[min(var(--measure-dialog-wide),calc(100vw-32px))]'
            : 'w-[min(var(--measure-confirm),calc(100vw-32px))]',
          className
        )}
        {...ariaName}
        onCancel={handleCancel}
        onClick={handleBackdropClick}
      >
        {/* One frame fills the dialog, and the blocks inside are spaced with
            padding, never margin: a click on an uncovered spot of the <dialog>
            itself is indistinguishable from a backdrop click and would close it. */}
        <div data-kiao-modal-frame className="pb-[var(--gap-md)]">
          <div data-kiao-modal-head className="px-[var(--inset-confirm)] pt-[var(--inset-confirm)]">
            {closeButton ? (
              // The × shares a row with the title only, pulled into the corner so
              // its glyph lines up with the title; the subtitle keeps the full width.
              <div className="flex items-start justify-between gap-[var(--gap-xs)]">
                <h3
                  id={titleId}
                  className="min-w-0 text-[length:var(--fs-confirm-title)] font-semibold"
                >
                  {title}
                </h3>
                <IconButton
                  variant="bar"
                  aria-label={closeLabel}
                  onClick={onClose}
                  className="-mr-2 -mt-1.5 text-muted hover:text-foreground"
                >
                  <X aria-hidden="true" />
                </IconButton>
              </div>
            ) : (
              <h3 id={titleId} className="text-[length:var(--fs-confirm-title)] font-semibold">
                {title}
              </h3>
            )}
            {subtitle != null ? (
              <p
                className={cn(
                  'text-sm leading-[var(--leading-confirm-body)] text-muted',
                  // The × row already stands taller than the title alone.
                  closeButton ? 'mt-1' : 'mt-2'
                )}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
          <div
            data-kiao-modal-body
            className={cn('pt-[var(--gap-sm)]', flushBody ? 'px-0' : 'px-[var(--inset-confirm)]')}
          >
            {children}
          </div>
          {footer == null ? null : (
            <div
              data-kiao-modal-footer
              className="flex justify-end gap-[var(--gap-confirm-actions)] px-[var(--inset-confirm)] pt-[var(--gap-md)]"
            >
              {footer}
            </div>
          )}
        </div>
      </dialog>
    )
  }

  return (
    <dialog
      ref={ref}
      data-kiao-modal
      data-kiao-modal-size={size}
      tabIndex={-1}
      className={cn(
        'kiao-modal m-auto outline-none w-[min(440px,calc(100vw-48px))] overflow-hidden rounded-[var(--radius-lg)] border border-border bg-background p-0 text-foreground shadow-[var(--shadow-dialog)] backdrop:bg-black/40',
        size === 'wide' &&
          'kiao-modal--wide w-[min(640px,calc(100vw-24px))] max-w-[calc(100vw-24px)]',
        scrollBody &&
          'kiao-modal--scroll-body max-h-[calc(100dvh-32px)] grid grid-rows-[auto_minmax(0,1fr)_auto]',
        className
      )}
      // When the head is collapsed (`.is-done` flash) its `<h3 id={titleId}>`
      // leaves the a11y tree, so `aria-labelledby` would name the dialog with
      // nothing. Fall back to an explicit `aria-label` in that case.
      {...ariaName}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
    >
      <div
        data-kiao-modal-head
        className="km-head flex items-start gap-[var(--gap-sm)] px-[var(--inset-modal-inline)] pt-[var(--inset-modal-head-top)]"
      >
        <div>
          <h3 id={titleId} className="text-xl font-semibold">
            {title}
          </h3>
          {subtitle != null ? <p className="km-sub mt-1 text-sm text-muted">{subtitle}</p> : null}
        </div>
        {headAccessory ?? (
          <button type="button" className="km-close" aria-label={closeLabel} onClick={onClose}>
            <X aria-hidden="true" className="size-4" />
          </button>
        )}
      </div>
      <div
        data-kiao-modal-body
        className={cn(
          'km-body px-[var(--inset-modal-inline)] pt-[var(--inset-modal-body-top)] pb-[var(--inset-modal-body-bottom)]',
          scrollBody && 'min-h-0 overflow-auto'
        )}
      >
        {children}
      </div>
      {footer == null ? null : (
        <div
          data-kiao-modal-footer
          className="km-foot flex items-center gap-2 border-t border-border px-3.5 py-3 min-[601px]:gap-2.5 min-[601px]:px-[var(--inset-modal-inline)] min-[601px]:py-[var(--inset-modal-footer-block)]"
        >
          {footer}
        </div>
      )}
    </dialog>
  )
}
